import { SearchableSelectField } from "@certn/cascadia-react";
import { usePeopleAPI } from "@certn/legacy-api";
import { Field } from "formik";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Item } from "react-stately";

type ProvinceSelectProps = {
  name: string;
  label: ReactNode;
  country?: string;
  testID?: string;
};

export function ProvinceSelect({
  name,
  label,
  country,
  testID = "province-select",
}: ProvinceSelectProps) {
  const { t } = useTranslation();
  const { data: subdivisions, isLoading: subdivisionsLoading } = usePeopleAPI(
    "/internal/people/addresses/country-subdivisions/",
    { alpha_2: country ?? "CA" }
  );

  const subdivisionKeysGB = ["Country", "Province", "State", "Territory"];
  const subdivisionKeysAll = [
    "Country",
    "Province",
    "State",
    "Territory",
    "District",
    "Union territory",
    "Outlying area",
  ];
  const subdivisionKeys =
    country === "GB" ? subdivisionKeysGB : subdivisionKeysAll;

  const isCanadaOrUS = country === "CA" || country === "US";

  const sortedSubdivisions = useMemo(
    () =>
      subdivisions
        ?.filter((subdivision) => subdivisionKeys.includes(subdivision.type))
        .map((subdivision) => ({
          ...subdivision,
          name: subdivision.name.split(";")[0], // cleanup names (ex. "Wales; Cymru")
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [subdivisions, subdivisionKeys]
  );

  return (
    <Field
      testID={testID}
      name={name}
      label={label}
      as={SearchableSelectField}
      isRequired={!!subdivisions?.length}
      isDisabled={subdivisionsLoading || !subdivisions?.length}
      placeholder={
        subdivisions?.length ? t("Please select") : t("Not available")
      }
      allowNull={false}
      validate={(value: string) => {
        if (isCanadaOrUS && !value) {
          return t("Please select a province or state.");
        }
      }}
    >
      {sortedSubdivisions?.map((subdivision) => (
        <Item key={subdivision.code} textValue={subdivision.name}>
          {subdivision.name}
        </Item>
      ))}
    </Field>
  );
}
